/* The state and server calls behind the Add contract window in Legal
 * (Chris's walk-through, 2026-10-19). The same hook as HMS's, so the two
 * systems behave alike and differ only in markup.
 *
 * Choosing the file uploads it as a draft at once; the server reads the
 * expiry date from it in the background and this hook polls for the answer.
 * When it lands and the person has not typed their own date yet, it fills
 * the expiry box and says where it came from. Saving never waits for it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import legalContractService, { legalRefusal } from '../../services/legalContractService';
import { fetchHMSEntities } from '../../services/hmsEntityService';

export const POLL_MS = 3000;
const READING = ['queued', 'processing'];

const empty = () => ({
  entity: '',
  partner: null, // CRM partner picked in the search
  notInCrm: false, // the party is typed instead
  typedParty: '',
  status: 'in_progress',
  expiryDate: '',
  noExpiry: false,
});

export default function useAddContract({ isOpen, onAdded }) {
  const [form, setForm] = useState(empty);
  const [entities, setEntities] = useState([]);
  // The company list answered (an empty answer means it could not load:
  // fetchHMSEntities turns a refusal into []).
  const [entitiesLoaded, setEntitiesLoaded] = useState(false);
  const [draft, setDraft] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Where the expiry in the box came from: 'ai' (the read filled it) or
  // 'person' (typed or ticked). The read never overwrites a person's answer.
  const [expirySource, setExpirySource] = useState(null);
  const sourceRef = useRef(null);
  sourceRef.current = expirySource;
  // Bumped whenever the window closes: an upload answer from before that
  // belongs to a file nobody is adding any more and is dropped.
  const sessionRef = useRef(0);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setEntitiesLoaded(false);
    fetchHMSEntities().then((list) => {
      if (cancelled) return;
      setEntities(list || []);
      setEntitiesLoaded(true);
    });
    return () => { cancelled = true; };
  }, [isOpen]);

  const reset = useCallback(() => {
    sessionRef.current += 1;
    setUploading(false);
    setForm(empty());
    setDraft(null);
    setError('');
    setExpirySource(null);
  }, []);

  useEffect(() => { if (!isOpen) reset(); }, [isOpen, reset]);

  const applySuggestion = useCallback((data) => {
    const s = data && data.suggestion;
    if (!s || sourceRef.current === 'person') return;
    if (s.expiry_date || s.no_expiry) {
      setForm((f) => ({ ...f, expiryDate: s.expiry_date || '', noExpiry: Boolean(s.no_expiry) }));
      setExpirySource('ai');
    }
  }, []);

  // Poll the draft while the server reads it.
  const draftId = draft && draft.id;
  const reading = Boolean(draft && READING.includes(draft.read_status));
  useEffect(() => {
    if (!draftId || !reading) return undefined;
    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const data = await legalContractService.getDraft(draftId);
        if (cancelled) return;
        setDraft(data);
        if (data.read_status === 'completed') applySuggestion(data);
      } catch (_err) {
        /* keep polling; the person can type the date meanwhile */
      }
    }, POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [draftId, reading, applySuggestion]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const chooseFile = async (file) => {
    setError('');
    // A date the AI read from the previous file is not this file's date.
    if (sourceRef.current === 'ai') {
      setForm((f) => ({ ...f, expiryDate: '', noExpiry: false }));
      setExpirySource(null);
    }
    // Stop reading the previous file: a late answer about it must not land
    // on this one.
    setDraft(null);
    if (!file) return;
    setUploading(true);
    const session = sessionRef.current;
    try {
      const data = await legalContractService.uploadDraft(file);
      if (session !== sessionRef.current) return;
      setDraft(data);
      if (data.read_status === 'completed') applySuggestion(data);
    } catch (err) {
      if (session !== sessionRef.current) return;
      setDraft(null);
      setError(legalRefusal(err, 'The file could not be added. Please try again.'));
    } finally {
      if (session === sessionRef.current) setUploading(false);
    }
  };

  const setExpiryDate = (value) => {
    setForm((f) => ({ ...f, expiryDate: value || '', noExpiry: false }));
    setExpirySource('person');
  };

  const setNoExpiry = (checked) => {
    setForm((f) => ({ ...f, noExpiry: checked, expiryDate: checked ? '' : f.expiryDate }));
    setExpirySource('person');
  };

  const setNotInCrm = (checked) => setForm((f) => ({ ...f, notInCrm: checked, partner: checked ? null : f.partner }));

  // What is still missing, in the words the server would use; '' when ready.
  const missing = (() => {
    if (!draft) return 'Add the contract file.';
    if (!form.entity) return 'Choose our company on this contract.';
    if (form.notInCrm ? !form.typedParty.trim() : !form.partner) {
      return form.notInCrm
        ? 'Type the other party’s name.'
        : 'Choose the other party from CRM, or tick "Not in CRM" and type their name.';
    }
    if (!form.noExpiry && !form.expiryDate) return 'Enter the expiry date, or tick "No expiry / rolls over" if it has none.';
    return '';
  })();

  const submit = async () => {
    if (missing) { setError(missing); return null; }
    setSaving(true);
    setError('');
    try {
      const contract = await legalContractService.addContract({
        draft_id: draft.id,
        entity: Number(form.entity),
        counterparty: form.notInCrm ? null : form.partner.id,
        counterparty_text: form.notInCrm ? form.typedParty.trim() : '',
        status: form.status,
        expiry_date: form.noExpiry ? null : form.expiryDate,
        no_expiry: form.noExpiry,
      });
      if (onAdded) onAdded(contract);
      return contract;
    } catch (err) {
      setError(legalRefusal(err, 'The contract could not be added. Please try again.'));
      return null;
    } finally {
      setSaving(false);
    }
  };

  return {
    form, set, entities, entitiesLoaded, draft, uploading, saving, error, missing, reading,
    expirySource, chooseFile, setExpiryDate, setNoExpiry, setNotInCrm, submit,
  };
}
